import React, { useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import { getNationCoordinates } from '../../constants/nationCoordinates';
import { getNationByName } from '../../constants/nations';
import { getPreferredTileStyle } from '../../constants/mapTiles';
import europeanBorders from '../../data/europeanBorders.json';
import 'leaflet/dist/leaflet.css';
import './MEDEVACRouteMap.css';

// Fix for default marker icons in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Hub marker icon
const HubIcon = L.icon({
  iconUrl: '/icons/hub_ucpm-eu.png',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14]
});

// Smaller hub icon for mission exit hubs
const ExitHubIcon = L.icon({
  iconUrl: '/icons/hub_ucpm-eu.png',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  popupAnchor: [0, -11]
});

// ── GeoJSON country polygon helpers ──

interface GeoFeature {
  type: string;
  properties: Record<string, string | null>;
  geometry: {
    type: string;
    coordinates: number[][][] | number[][][][];
  };
}

interface GeoFeatureCollection {
  type: string;
  features: GeoFeature[];
}

/** Find the GeoJSON feature for a country by display name */
function getCountryFeature(nationName: string): GeoFeature | null {
  const geoData = europeanBorders as unknown as GeoFeatureCollection;
  const lower = nationName.toLowerCase();

  for (const feature of geoData.features) {
    const p = feature.properties;
    if (
      p.name?.toLowerCase() === lower ||
      p.name_en?.toLowerCase() === lower ||
      p.admin?.toLowerCase() === lower ||
      p.name_long?.toLowerCase() === lower
    ) {
      return feature;
    }
  }
  return null;
}

/** Extract bounding box corners [SW, NE] from a GeoJSON feature */
function getFeatureBoundsCorners(feature: GeoFeature): [number, number][] {
  let minLat = Infinity, maxLat = -Infinity;
  let minLng = Infinity, maxLng = -Infinity;

  const visit = (arr: unknown): void => {
    if (Array.isArray(arr)) {
      if (arr.length >= 2 && typeof arr[0] === 'number' && typeof arr[1] === 'number') {
        // [lng, lat] in GeoJSON
        const lng = arr[0] as number;
        const lat = arr[1] as number;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
      } else {
        arr.forEach(visit);
      }
    }
  };

  visit(feature.geometry.coordinates);

  return [
    [minLat, minLng], // SW
    [maxLat, maxLng], // NE
  ];
}

/**
 * Injects an SVG <pattern> with the flag image into the Leaflet SVG renderer.
 * This allows GeoJSON polygons to use `fillColor: 'url(#patternId)'`
 * so the flag texture is clipped to the exact country shape.
 */
function FlagPatternDef({ patternId, flagUrl }: { patternId: string; flagUrl: string }) {
  const map = useMap();

  useEffect(() => {
    // Leaflet's SVG renderer lives inside the overlay pane
    const svgEl = map.getPane('overlayPane')?.querySelector('svg');
    if (!svgEl) return;

    // Ensure <defs> exists
    let defs = svgEl.querySelector('defs');
    if (!defs) {
      defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      svgEl.prepend(defs);
    }

    // Create <pattern> with flag <image> filling the polygon bounding box
    const pattern = document.createElementNS('http://www.w3.org/2000/svg', 'pattern');
    pattern.setAttribute('id', patternId);
    pattern.setAttribute('patternUnits', 'objectBoundingBox');
    pattern.setAttribute('patternContentUnits', 'objectBoundingBox');
    pattern.setAttribute('width', '1');
    pattern.setAttribute('height', '1');

    const image = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    image.setAttributeNS('http://www.w3.org/1999/xlink', 'href', flagUrl);
    image.setAttribute('x', '0');
    image.setAttribute('y', '0');
    image.setAttribute('width', '1');
    image.setAttribute('height', '1');
    image.setAttribute('preserveAspectRatio', 'xMidYMid slice');

    pattern.appendChild(image);
    defs.appendChild(pattern);

    return () => {
      pattern.remove();
    };
  }, [map, patternId, flagUrl]);

  return null;
}

interface MissionRouteInfo {
  nation: string;
  missionId: string;
  missionType: string;
  patientCount: number;
  exitHub?: {
    name: string;
    latitude: number;
    longitude: number;
  } | null;
}

interface MEDEVACRouteMapProps {
  creatorNation: string;
  exitPoint: {
    name: string;
    nation?: string;
    latitude: number;
    longitude: number;
  };
  missions?: any[];
}

// Component to fit bounds with padding
function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();

  useEffect(() => {
    if (points.length < 2) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, {
      padding: [50, 50],
      maxZoom: 6
    });
  }, [map, points]);

  return null;
}

// Distinct colors for mission routes
const MISSION_COLORS = [
  '#28a745', // green
  '#e67e22', // orange
  '#9b59b6', // purple
  '#e74c3c', // red
  '#1abc9c', // teal
  '#f39c12', // amber
];

function MEDEVACRouteMap({ creatorNation, exitPoint, missions }: MEDEVACRouteMapProps) {
  const nationCoords = getNationCoordinates(creatorNation);

  // Calculate distance
  const distance = useMemo(() => {
    if (!nationCoords) return 0;

    const [nationLat, nationLng] = nationCoords;
    const hubLat = exitPoint.latitude;
    const hubLng = exitPoint.longitude;

    const R = 6371;
    const dLat = (hubLat - nationLat) * Math.PI / 180;
    const dLon = (hubLng - nationLng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(nationLat * Math.PI / 180) * Math.cos(hubLat * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return Math.round(R * c);
  }, [nationCoords, exitPoint]);

  // Calculate initial center
  const initialCenter = useMemo(() => {
    if (!nationCoords) {
      return [exitPoint.latitude, exitPoint.longitude] as [number, number];
    }
    return [
      (nationCoords[0] + exitPoint.latitude) / 2,
      (nationCoords[1] + exitPoint.longitude) / 2
    ] as [number, number];
  }, [nationCoords, exitPoint]);

  // Extract mission route info — deduplicate by nation (one line per nation)
  const missionRoutes = useMemo((): MissionRouteInfo[] => {
    if (!missions || missions.length === 0) return [];

    const nationMap = new Map<string, MissionRouteInfo>();

    missions.forEach(m => {
      // Skip child missions (parent handles them)
      if (m.parentMissionId) return;
      // Skip TREATMENT-only missions (the transport mission draws the line)
      if (m.missionType === 'TREATMENT') return;

      const key = m.nation;
      if (nationMap.has(key)) {
        // Aggregate patient count
        const existing = nationMap.get(key)!;
        existing.patientCount += (m.patients?.length || 0);
        // Prefer an exit hub if this mission has one
        const rsp = m.offer?.receivingStartPoint;
        if (!existing.exitHub && rsp && rsp.latitude != null && rsp.longitude != null) {
          existing.exitHub = {
            name: rsp.name,
            latitude: rsp.latitude,
            longitude: rsp.longitude,
          };
        }
        return;
      }

      const sp = m.offer?.receivingStartPoint;
      nationMap.set(key, {
        nation: m.nation,
        missionId: m.missionId,
        missionType: m.missionType || 'BOTH',
        patientCount: m.patients?.length || 0,
        exitHub: sp && sp.latitude != null && sp.longitude != null ? {
          name: sp.name,
          latitude: sp.latitude,
          longitude: sp.longitude,
        } : null
      });
    });

    return Array.from(nationMap.values());
  }, [missions]);

  // Lookup GeoJSON feature for requesting nation
  const nationFeature = useMemo(() => getCountryFeature(creatorNation), [creatorNation]);

  // Collect all significant points for FitBounds.
  // Use capital coords (not polygon extremes) so the map frames the route
  // without zooming out excessively just to show distant polygon edges.
  const allPoints = useMemo((): [number, number][] => {
    const pts: [number, number][] = [];

    // Requesting nation capital
    if (nationCoords) {
      pts.push(nationCoords);
    }

    // Transit hub
    pts.push([exitPoint.latitude, exitPoint.longitude]);

    // Mission destinations (exit hub coords + nation capital)
    missionRoutes.forEach(route => {
      if (route.exitHub) {
        pts.push([route.exitHub.latitude, route.exitHub.longitude]);
      }
      const coords = getNationCoordinates(route.nation);
      if (coords) pts.push(coords);
    });

    return pts;
  }, [nationCoords, exitPoint, missionRoutes]);

  if (!nationCoords) {
    return (
      <div className="route-map-error">
        <p>Coordinates not available for {creatorNation}</p>
      </div>
    );
  }

  const hubCoords: [number, number] = [exitPoint.latitude, exitPoint.longitude];
  const hasMissions = missionRoutes.length > 0;

  return (
    <div className="medevac-route-map">
      <h4>MEDEVAC Route</h4>
      <MapContainer
        center={initialCenter}
        zoom={5}
        scrollWheelZoom={false}
        dragging={true}
        zoomControl={true}
        style={{ height: hasMissions ? '300px' : '250px', width: '100%', borderRadius: '6px' }}
      >
        <TileLayer
          url={getPreferredTileStyle().url}
          attribution={getPreferredTileStyle().attribution}
          errorTileUrl="/tiles/fallback.png"
        />

        <FitBounds points={allPoints} />

        {/* Blue line from requesting nation to transit hub */}
        <Polyline
          positions={[nationCoords, hubCoords]}
          pathOptions={{
            color: 'rgba(0, 51, 153, 0.6)',
            weight: 8,
            opacity: 0.6
          }}
        />

        {/* Requesting nation — country polygon with flag texture clipped to shape */}
        {nationFeature ? (
          <>
            {(() => {
              const nation = getNationByName(creatorNation);
              const patternId = `flag-${creatorNation.replace(/\s+/g, '-').toLowerCase()}`;
              return nation ? <FlagPatternDef patternId={patternId} flagUrl={nation.flagImage} /> : null;
            })()}
            <GeoJSON
              key={`nation-${creatorNation}`}
              data={nationFeature as GeoJSON.GeoJsonObject}
              style={{
                fillColor: `url(#flag-${creatorNation.replace(/\s+/g, '-').toLowerCase()})`,
                fillOpacity: 0.35,
                color: '#003399',
                weight: 2,
                opacity: 0.7,
              }}
              onEachFeature={(_f, layer) => {
                layer.bindPopup(
                  `<strong>${creatorNation}</strong><br/>` +
                  `<span style="color:#003399">Requesting Nation</span>`
                );
              }}
            />
          </>
        ) : null}

        {/* Mission route lines + destination country polygons */}
        {missionRoutes.map((route, idx) => {
          const color = MISSION_COLORS[idx % MISSION_COLORS.length];
          const exitHub = route.exitHub;
          const nationFallback = getNationCoordinates(route.nation);
          const targetCoords: [number, number] | null = exitHub
            ? [exitHub.latitude, exitHub.longitude]
            : nationFallback;

          if (!targetCoords) return null;

          // Line target: prefer exit hub coords, then nation center
          const lineTarget = targetCoords || nationFallback;

          return (
            <React.Fragment key={`route-${route.nation}-${idx}`}>
              {/* Line from transit hub to destination */}
              {lineTarget && (
                <Polyline
                  positions={[hubCoords, lineTarget]}
                  pathOptions={{
                    color,
                    weight: 4,
                    opacity: 0.8,
                    dashArray: exitHub ? undefined : '8, 6'
                  }}
                />
              )}

              {/* Exit hub marker (specific location pin) */}
              {exitHub && (
                <Marker position={[exitHub.latitude, exitHub.longitude]} icon={ExitHubIcon}>
                  <Popup>
                    <strong>{exitHub.name}</strong><br/>
                    {route.nation} — {route.patientCount} patient{route.patientCount !== 1 ? 's' : ''}
                  </Popup>
                </Marker>
              )}
            </React.Fragment>
          );
        })}

        {/* Transit Hub Marker */}
        <Marker position={hubCoords} icon={HubIcon}>
          <Popup>
            <strong>{exitPoint.name}</strong><br/>
            UCPM Transit Hub{exitPoint.nation && ` (${exitPoint.nation})`}
          </Popup>
        </Marker>
      </MapContainer>

      <div className="route-info">
        <div className="route-info-item">
          <span className="route-label">Distance:</span>
          <span className="route-value">{distance} km</span>
        </div>
        <div className="route-info-item">
          <span className="route-label">From:</span>
          <span className="route-value">{creatorNation}</span>
        </div>
        <div className="route-info-item">
          <span className="route-label">To:</span>
          <span className="route-value">{exitPoint.name}</span>
        </div>
        {hasMissions && (
          <div className="mission-routes-legend">
            {missionRoutes.map((route, idx) => (
              <div key={route.nation} className="legend-item">
                <span
                  className="legend-color"
                  style={{ background: MISSION_COLORS[idx % MISSION_COLORS.length] }}
                />
                <span className="legend-text">
                  {route.nation} — {route.patientCount} pat.
                  {route.exitHub ? ` (${route.exitHub.name})` : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MEDEVACRouteMap;
